import { useCallback, useState } from "react";
import { Button, Flex, Text, TextInput } from "@jsq/ds/core";
import { SPACING } from "~/styles/variables";
import { HierarchyTreeGrid, type TypeHierarchy } from "./HierarchyTreeGrid";
import * as styles from "./styles_vanilla.css";

type HierarchyPanelProps = {
  title?: string;
  typeHierarchy: TypeHierarchy | null;
  onDownload: () => void;
  onUpdateHierarchy: () => void;
  isDownloading?: boolean;
  hideUpdateAction?: boolean;
};

export const HierarchyPanel = ({
  title = "Hierarchy",
  typeHierarchy,
  onDownload,
  onUpdateHierarchy,
  isDownloading,
  hideUpdateAction = false,
}: HierarchyPanelProps) => {
  const [searchValue, setSearchValue] = useState("");

  const handleSearchChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchValue(event.target.value);
  }, []);

  const handleClearSearch = useCallback(() => {
    setSearchValue("");
  }, []);

  return (
    <Flex flexDirection="column" className={styles.hierarchyPanel}>
      <Flex
        justifyContent="space-between"
        alignItems="center"
        gap={SPACING.MEDIUM}
        className={styles.hierarchyPanelHeader}
      >
        <Flex flexDirection="column" gap={SPACING.X_SMALL}>
          <Text variant="upper-md" className={styles.hierarchyDetailsTitle}>
            {title}
          </Text>
        </Flex>
        <Flex alignItems="center" gap={SPACING.SMALL}>
          <TextInput
            value={searchValue}
            onChange={handleSearchChange}
            placeholder="Search hierarchy"
            icon="search"
            isClearable
            onClearValue={handleClearSearch}
            data-testid="hierarchy-search"
          />
          <Button
            variant="neutral"
            icon="download"
            iconPosition="left"
            onClick={onDownload}
            isLoading={isDownloading}
          >
            Download hierarchy
          </Button>
          {!hideUpdateAction && (
            <Button
              variant="primary"
              icon="edit"
              onClick={onUpdateHierarchy}
              data-testid="update-hierarchy-btn"
            >
              Update hierarchy
            </Button>
          )}
        </Flex>
      </Flex>
      <Flex flexGrow={1} className={styles.hierarchyTableContainer}>
        <HierarchyTreeGrid typeHierarchy={typeHierarchy} searchValue={searchValue} />
      </Flex>
    </Flex>
  );
};
